/* Saundarya — carrito lateral.

   El elemento se llama `cart-drawer` a proposito y no `saundarya-cart-drawer`:
   product-form.js busca literalmente `cart-notification` o `cart-drawer`, y si
   no encuentra ninguno de los dos cae en `window.location = routes.cart_url`,
   o sea que cada "Agregar" recargaria la pagina entera. Manteniendo el nombre,
   el contrato de Dawn (getSectionsToRender / setActiveElement / renderContents)
   sigue valiendo tal cual para product-form.js y para saundarya-rutina.liquid.
   El cart-drawer de Dawn (assets/cart-drawer.js) no se carga en ningun sitio;
   aun asi se comprueba antes de registrar, por si alguien lo reintroduce.

   No hay ni una cuenta de dinero aqui. Cada cambio del carrito pide a Shopify
   la seccion ya renderizada y se sustituye el contenido: el subtotal lo calcula
   Liquid con el carrito de verdad. Ver la cabecera de
   sections/saundarya-cart-drawer.liquid.

   Todos los escuchadores van delegados sobre el propio elemento, asi que
   reemplazar innerHTML no obliga a volver a enlazar nada. */

if (!customElements.get('cart-drawer')) {
  customElements.define(
    'cart-drawer',
    class SaundaryaCartDrawer extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.abierto = false;
        this.ocupado = false;

        this.addEventListener('click', this.onClick.bind(this));
        this.addEventListener('submit', this.onSubmit.bind(this));
        this.addEventListener('change', this.onChange.bind(this));
        this.addEventListener('keyup', (evento) => {
          if (evento.code === 'Escape') this.close();
        });

        // El icono del carrito del header y cualquier otro disparador viven
        // fuera de este elemento, asi que su escucha va en el documento.
        if (!document.saundaryaCartOpenBound) {
          document.saundaryaCartOpenBound = true;
          document.addEventListener('click', (evento) => {
            const disparador = evento.target.closest('[data-sa-cart-open]');
            if (!disparador) return;
            const cajon = document.querySelector('cart-drawer');
            if (!cajon) return;
            evento.preventDefault();
            cajon.setActiveElement(disparador);
            cajon.open();
          });
        }

        // En el editor de temas el panel esta oculto, asi que al seleccionar la
        // seccion no se veria nada y el merchant estaria tocando ajustes a
        // ciegas. Se abre al seleccionarla y se cierra al salir. Los
        // escuchadores van en el documento y buscan el elemento vivo cada vez:
        // el editor sustituye la seccion entera al cambiar un ajuste, y guardar
        // `this` dejaria enganchado un nodo que ya no esta en la pagina.
        if (window.Shopify && window.Shopify.designMode && !document.saundaryaCartEditorBound) {
          document.saundaryaCartEditorBound = true;
          const enSeccion = (evento) => evento.target.querySelector('cart-drawer');
          document.addEventListener('shopify:section:select', (evento) => enSeccion(evento)?.open());
          document.addEventListener('shopify:section:deselect', (evento) => enSeccion(evento)?.close());
          document.addEventListener('shopify:section:load', (evento) => enSeccion(evento)?.open());
        }

        this.sincronizarDestino();
      }

      /* ---------------------------------------------------------------
         Contrato que espera Dawn
         --------------------------------------------------------------- */

      getSectionsToRender() {
        return [{ id: this.sectionId }];
      }

      setActiveElement(elemento) {
        this.activeElement = elemento;
      }

      async renderContents(estado) {
        const html = estado && estado.sections && estado.sections[this.sectionId];
        if (html) {
          this.pintar(html);
        } else {
          // Puede llegar una respuesta sin secciones (por ejemplo si alguien
          // llama sin pasar `sections`). Antes que abrir un carrito desfasado,
          // se vuelve a pedir.
          await this.refrescar();
        }
        this.open();
      }

      /* ---------------------------------------------------------------
         Abrir y cerrar
         --------------------------------------------------------------- */

      open() {
        if (this.abierto) return;
        this.abierto = true;
        this.hidden = false;
        document.body.classList.add('sa-cart-abierto');

        // Un reflow entre quitar `hidden` y poner la clase: sin el, el
        // navegador agrupa los dos cambios y el panel aparece de golpe en su
        // sitio en vez de deslizarse.
        this.offsetHeight;
        this.classList.add('sa-cart--abierto');

        const panel = this.querySelector('.sa-cart__panel');
        if (panel && typeof trapFocus === 'function') trapFocus(panel, panel);
      }

      close() {
        if (!this.abierto) return;
        this.abierto = false;
        this.classList.remove('sa-cart--abierto');
        document.body.classList.remove('sa-cart-abierto');

        if (typeof removeTrapFocus === 'function') removeTrapFocus(this.activeElement);

        // No se espera a transitionend: con prefers-reduced-motion no hay
        // transicion y el evento no llegaria nunca, dejando el velo capturando
        // clics sobre toda la pagina.
        clearTimeout(this.temporizadorCierre);
        this.temporizadorCierre = setTimeout(() => {
          if (!this.abierto) this.hidden = true;
        }, 250);
      }

      /* ---------------------------------------------------------------
         Interaccion
         --------------------------------------------------------------- */

      onClick(evento) {
        if (evento.target.closest('[data-sa-cart-close]')) {
          evento.preventDefault();
          this.close();
          return;
        }

        const paso = evento.target.closest('[data-sa-qty]');
        if (paso) {
          evento.preventDefault();
          this.cambiar(paso.dataset.key, Number(paso.dataset.saQty));
        }
      }

      onChange(evento) {
        const nota = evento.target.closest('[data-sa-nota]');
        if (nota) {
          this.guardarNota(nota.value);
          return;
        }

        const campo = evento.target.closest('[data-sa-qty-input]');
        if (!campo) return;
        // Suelo en 1, como declara el min del propio campo: escribir un 0 no
        // borra la linea. Quitar del carrito pasa siempre por "Quitar".
        const cantidad = Math.max(1, Math.floor(Number(campo.value) || 1));
        this.cambiar(campo.dataset.key, cantidad);
      }

      // La nota se guarda sin repintar: el evento `change` llega al salir del
      // campo, y sustituir el HTML en ese momento le robaria el foco a quien
      // esta tabulando hacia el boton de pagar. Ademas no cambia ningun
      // importe, asi que no hay nada que volver a pintar.
      async guardarNota(texto) {
        try {
          await fetch(window.routes.cart_update_url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
            body: JSON.stringify({ note: texto }),
          });
        } catch (error) {
          console.error('[sa-cart]', error);
        }
      }

      onSubmit(evento) {
        const formulario = evento.target.closest('[data-sa-upsell]');
        if (!formulario) return;
        evento.preventDefault();
        const id = formulario.querySelector('[name="id"]');
        if (id) this.agregar(id.value, evento.submitter);
      }

      /* ---------------------------------------------------------------
         Peticiones
         --------------------------------------------------------------- */

      async cambiar(clave, cantidad) {
        if (this.ocupado || !clave) return;
        await this.pedir(window.routes.cart_change_url, { id: clave, quantity: cantidad });
      }

      async agregar(idVariante, boton) {
        if (this.ocupado || !idVariante) return;
        if (boton) boton.setAttribute('aria-disabled', 'true');
        await this.pedir(window.routes.cart_add_url, { items: [{ id: Number(idVariante), quantity: 1 }] });
      }

      async pedir(url, cuerpo) {
        this.ocupado = true;
        this.classList.add('sa-cart--ocupado');

        try {
          const respuesta = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
            body: JSON.stringify({
              ...cuerpo,
              sections: [this.sectionId],
              sections_url: window.location.pathname,
            }),
          });
          const datos = await respuesta.json();

          if (datos.sections && datos.sections[this.sectionId]) {
            this.pintar(datos.sections[this.sectionId]);
          } else {
            await this.refrescar();
          }

          // Lo publica para el contador del header y la ficha de producto, que
          // ya estaban suscritos.
          if (typeof publish === 'function') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'saundarya-cart-drawer', cartData: datos });
          }
        } catch (error) {
          console.error('[sa-cart]', error);
          await this.refrescar();
        } finally {
          this.ocupado = false;
          this.classList.remove('sa-cart--ocupado');
        }
      }

      // Ultimo recurso: se pide la pagina entera y se saca de ahi el carrito.
      // Cuesta mas que `?sections=`, pero no depende de nada mas que de que la
      // pagina siga existiendo.
      async refrescar() {
        try {
          const url = new URL(window.location.href);
          url.searchParams.set('sections', this.sectionId);
          const datos = await fetch(url.toString()).then((r) => r.json());
          if (datos && datos[this.sectionId]) {
            this.pintar(datos[this.sectionId]);
            return;
          }
        } catch (error) {
          /* se intenta con la pagina completa */
        }

        try {
          const html = await fetch(window.location.pathname).then((r) => r.text());
          const nuevo = new DOMParser().parseFromString(html, 'text/html').querySelector('cart-drawer');
          if (nuevo) this.pintar(nuevo.innerHTML, true);
        } catch (error) {
          console.error('[sa-cart]', error);
        }
      }

      pintar(html, esInterior = false) {
        const contenido = esInterior
          ? html
          : new DOMParser().parseFromString(html, 'text/html').querySelector('cart-drawer').innerHTML;

        // Se conserva lo que el cliente estaba escribiendo: la nota es el unico
        // campo libre y perderla en cada cambio de cantidad seria inaceptable.
        const notaPrevia = this.querySelector('[data-sa-nota]');
        const textoNota = notaPrevia ? notaPrevia.value : null;
        const notaAbierta = notaPrevia ? notaPrevia.closest('details').open : false;

        this.innerHTML = contenido;

        if (textoNota !== null) {
          const nota = this.querySelector('[data-sa-nota]');
          if (nota) {
            nota.value = textoNota;
            if (notaAbierta) nota.closest('details').open = true;
          }
        }

        this.sincronizarDestino();
      }

      /* ---------------------------------------------------------------
         Coherencia del boton de pagar

         assets/whatsapp-checkout.js intercepta todo button[name="checkout"] y
         lo manda a WhatsApp. Si ese archivo se borra (su propia cabecera dice
         que es temporal) y nadie se acuerda de cambiar el ajuste, el boton
         seguiria diciendo "Finalizar por WhatsApp" mientras lleva al checkout
         de Shopify. Aqui manda lo que hace el codigo, no lo que dice el ajuste.
         --------------------------------------------------------------- */

      sincronizarDestino() {
        const cta = this.querySelector('[data-sa-cta]');
        if (!cta) return;

        const real = window.saundaryaWhatsappCheckout === true ? 'whatsapp' : 'shopify';
        if (real === this.dataset.destino) return;

        const aviso = this.querySelector('[data-sa-aviso]');
        if (real === 'whatsapp') {
          cta.textContent = this.dataset.ctaWhatsapp;
          if (aviso) aviso.textContent = this.dataset.avisoWhatsapp;
        } else {
          // El total se toma del que ya pinto Liquid, no se recalcula: asi no
          // hay dos formatos de moneda distintos conviviendo en el panel.
          const total = this.querySelector('.sa-cart__total');
          cta.textContent = (this.dataset.ctaShopify || '').replace('[total]', total ? total.textContent.trim() : '');
          if (aviso) aviso.textContent = this.dataset.avisoShopify;
        }
      }
    }
  );
}
